/**
 * pages/MyForms.tsx
 *
 * Unified form templates page (table view) for both regular users and admins.
 * Regular users see their own forms; admins see admin-created forms (created_by IS NOT NULL)
 * plus any forms they personally own (user_id = admin.id) - user-created custom forms
 * (created_by IS NULL) owned by another user are excluded. The admin view includes a
 * "Utilisateur" column.
 * Changes: seed notifMap from unreadNotificationMap so has_unread_assignment stays correct on fetch failure;
 * sort allTemplates by updated_at descending after merge.
 * Admins control form visibility via the toggle visibility action.
 * Updated 2026-03-16: removed assign/unassign/duplicate logic; simplified to send, preview,
 *   edit, toggle visibility, and delete. Admin preview hides the send button.
 * Updated 2026-04-16: re-introduced multi-user assignment. Regular users now also see
 *   templates assigned to them (is_assigned=true, read-only). Admins see assignment counts
 *   and have an Attribuer action. AssignFormModal handles the assignment workflow.
 * Updated 2026-04-16: unread assignment indicator - blue dot on newly assigned forms.
 *   Reads unread form_assigned notifications; clears dot when user interacts with the form.
 * Updated 2026-04-17: admin query fixed - admins now see all forms where created_by IS NOT NULL
 *   (admin-created forms, regardless of which user owns them) plus any forms they personally own
 *   (user_id = admin.id). User-created custom forms (created_by IS NULL, owned by another user)
 *   are excluded. Previously the filter was user_id = admin.id only, which hid all admin-created
 *   forms assigned to other users.
 * Updated Apr 2026: handleMarkRead now guards against Supabase update failures before
 *   clearing local unread state.
 * Updated Apr 2026: AssignFormModal onClose now accepts a success flag; fetchTemplates
 *   is only called when the flag is true, avoiding unnecessary refetches on cancel.
 * Updated Apr 2026: notifMap starts fresh on each fetch to prevent duplicate notification IDs
 *   accumulating across re-fetches; error path falls back to previous unreadNotificationMap.
 * Updated Apr 2026: handleMarkRead now scopes the notification update to the current user via an
 *   additional .eq('user_id', user.id) filter.
 * Updated May 2026: removed is_visible_to_user filter from the assigned-templates query and from
 *   the downstream client-side filter. Visibility for assigned templates is governed solely by
 *   form_template_assignments.is_active; is_visible_to_user only applies to a user's own templates.
 * Updated Jun 2026: uses useEffectiveUser so the admin sees the impersonated user's forms in
 *   impersonation mode; write operations are blocked unless isWriteUnlocked.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useEffectiveUser } from '../hooks/useEffectiveUser';
import { useToast } from '../contexts/ToastContext';
import { supabase } from '../lib/supabase';
import { captureError } from '../utils/errorReporting';
import AppLayout from '../components/layout/AppLayout';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import FormsTable, { TemplateWithMeta } from '../components/forms/FormsTable';
import SendFormModal from '../components/forms/SendFormModal';
import FormTemplatePreviewModal from '../components/forms/FormTemplatePreviewModal';
import AssignFormModal from '../components/forms/AssignFormModal';
import { ConfirmModal } from '../components/ui/ConfirmModal';
import Spinner from '../components/ui/Spinner';
import { Plus, FileText, Search, SearchX } from 'lucide-react';
import type { Database } from '../types/database';

type FormTemplate = Database['public']['Tables']['form_templates']['Row'];

export default function MyForms() {
  const { user, userProfile } = useAuth();
  const { effectiveUserId, effectiveIsAdmin, isImpersonating, canWrite } = useEffectiveUser();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const isAdmin = effectiveIsAdmin;

  const [templates, setTemplates] = useState<TemplateWithMeta[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateWithMeta | null>(null);

  const [previewTemplateId, setPreviewTemplateId] = useState<string | null>(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [templateToAssign, setTemplateToAssign] = useState<TemplateWithMeta | null>(null);

  const [fullSelectedTemplate, setFullSelectedTemplate] = useState<FormTemplate | null>(null);
  const [sendingId, setSendingId] = useState<string | null>(null);

  const [unreadNotificationMap, setUnreadNotificationMap] = useState<Map<string, string[]>>(new Map());

  useEffect(() => {
    if (user && userProfile !== null && effectiveUserId) fetchTemplates();
  }, [user, userProfile, effectiveUserId]);

  const fetchTemplates = async () => {
    if (!effectiveUserId) return;
    setLoading(true);
    try {
      let ownedTemplates: FormTemplate[] = [];
      let assignedTemplates: FormTemplate[] = [];
      let userEmailMap = new Map<string, string>();
      let assignmentCountMap = new Map<string, number>();

      if (isAdmin) {
        const [{ data: tData, error: tError }, { data: uData, error: uError }, { data: aData, error: aError }] =
          await Promise.all([
            supabase.from('form_templates').select('*').or(`created_by.not.is.null,user_id.eq.${effectiveUserId}`).order('updated_at', { ascending: false }),
            supabase.from('users').select('id, email').eq('is_admin', false),
            supabase.from('form_template_assignments').select('template_id').eq('is_active', true),
          ]);

        if (tError) throw tError;
        if (uError) throw uError;
        if (aError) throw aError;

        ownedTemplates = tData || [];
        (uData || []).forEach((u) => { userEmailMap.set(u.id, u.email); });
        (aData || []).forEach((a) => {
          assignmentCountMap.set(a.template_id, (assignmentCountMap.get(a.template_id) ?? 0) + 1);
        });
      } else {
        const { data: ownData, error: ownError } = await supabase
          .from('form_templates')
          .select('*')
          .eq('user_id', effectiveUserId)
          .order('updated_at', { ascending: false });

        if (ownError) throw ownError;
        ownedTemplates = (ownData || []).filter((t) => t.is_visible_to_user);

        const { data: assignData, error: assignError } = await supabase
          .from('form_template_assignments')
          .select('template_id')
          .eq('user_id', effectiveUserId)
          .eq('is_active', true);

        if (assignError) throw assignError;

        const assignedIds = (assignData || []).map((a) => a.template_id);
        const ownedIds = new Set(ownedTemplates.map((t) => t.id));
        const newAssignedIds = assignedIds.filter((id) => !ownedIds.has(id));

        if (newAssignedIds.length > 0) {
          const { data: atData, error: atError } = await supabase
            .from('form_templates')
            .select('*')
            .in('id', newAssignedIds)
            .eq('status', 'active');

          if (atError) throw atError;
          assignedTemplates = atData || [];
        }
      }

      const allTemplates = [...ownedTemplates, ...assignedTemplates];
      allTemplates.sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());
      const templateIds = allTemplates.map((t) => t.id);

      const { data: lastSentData, error: lastSentError } = templateIds.length
        ? await supabase
            .from('requests')
            .select('template_id, created_at')
            .in('template_id', templateIds)
            .order('created_at', { ascending: false })
        : { data: [], error: null };

      if (lastSentError) throw lastSentError;

      const lastSentMap = new Map<string, string>();
      (lastSentData || []).forEach((r) => {
        if (!lastSentMap.has(r.template_id)) {
          lastSentMap.set(r.template_id, r.created_at);
        }
      });

      const ownedIds = new Set(ownedTemplates.map((t) => t.id));

      let notifMap = new Map<string, string[]>();
      if (!isAdmin && effectiveUserId && !isImpersonating) {
        const { data: notifData, error: notifError } = await supabase
          .from('notifications')
          .select('id, metadata')
          .eq('user_id', effectiveUserId)
          .eq('type', 'form_assigned')
          .eq('is_read', false);

        if (notifError) {
          console.error('[MyForms] Failed to fetch unread notifications:', notifError);
          notifMap = new Map(unreadNotificationMap);
        } else {
          (notifData || []).forEach((n) => {
            const templateId = (n.metadata as Record<string, unknown>)?.template_id as string | undefined;
            if (templateId) {
              const existing = notifMap.get(templateId) ?? [];
              notifMap.set(templateId, [...existing, n.id]);
            }
          });
          setUnreadNotificationMap(notifMap);
        }
      } else {
        setUnreadNotificationMap(notifMap);
      }

      const isTemplateVisible = (t: { id: string; is_visible_to_user: boolean }) => {
        if (isAdmin) return true;
        if (ownedIds.has(t.id)) return t.is_visible_to_user;
        return true;
      };

      const result: TemplateWithMeta[] = allTemplates
        .filter(isTemplateVisible)
        .map((t) => ({
          id: t.id,
          name: t.name,
          status: t.status,
          created_by: t.created_by,
          user_id: t.user_id,
          is_visible_to_user: t.is_visible_to_user,
          updated_at: t.updated_at,
          last_sent_at: lastSentMap.get(t.id) || null,
          owner_email: userEmailMap.get(t.user_id) || null,
          is_assigned: !ownedIds.has(t.id),
          assignment_count: assignmentCountMap.get(t.id) ?? 0,
          has_unread_assignment: notifMap.has(t.id),
        }));

      setTemplates(result);
    } catch (err) {
      console.error('Error fetching templates:', err);
      captureError(err, { page: 'MyForms', action: 'fetchTemplates', userId: effectiveUserId });
      showToast('Erreur lors du chargement des formulaires', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!templateToDelete) return;
    if (!canWrite) {
      showToast('Déverrouillez les actions pour supprimer des formulaires', 'error');
      return;
    }
    setDeletingId(templateToDelete);
    try {
      const { error } = await supabase
        .from('form_templates')
        .delete()
        .eq('id', templateToDelete);
      if (error) throw error;

      setTemplates((prev) => prev.filter((t) => t.id !== templateToDelete));
      showToast('Formulaire supprimé avec succès', 'success');
      setDeleteModalOpen(false);
      setTemplateToDelete(null);
    } catch (err) {
      console.error('Error deleting template:', err);
      captureError(err, { page: 'MyForms', action: 'handleDeleteConfirm', templateId: templateToDelete });
      showToast('Erreur lors de la suppression du formulaire', 'error');
    } finally {
      setDeletingId(null);
    }
  };

  const handleToggleVisibility = async (template: TemplateWithMeta) => {
    const newValue = !template.is_visible_to_user;
    try {
      const { error } = await supabase
        .from('form_templates')
        .update({ is_visible_to_user: newValue })
        .eq('id', template.id);
      if (error) throw error;
      setTemplates((prev) =>
        prev.map((t) => (t.id === template.id ? { ...t, is_visible_to_user: newValue } : t))
      );
      showToast(
        newValue ? "Formulaire rendu visible pour l'utilisateur" : "Formulaire masqué pour l'utilisateur",
        'success'
      );
    } catch (err) {
      console.error('Error toggling visibility:', err);
      captureError(err, { page: 'MyForms', action: 'handleToggleVisibility', templateId: template.id });
      showToast('Erreur lors de la modification de la visibilité', 'error');
    }
  };

  const handleSend = async (template: TemplateWithMeta) => {
    if (!canWrite) {
      showToast('Déverrouillez les actions pour envoyer des formulaires', 'error');
      return;
    }
    setSelectedTemplate(template);
    setSendingId(template.id);
    try {
      const { data, error } = await supabase
        .from('form_templates')
        .select('*')
        .eq('id', template.id)
        .single();
      if (error) throw error;
      setFullSelectedTemplate(data);
      setSendModalOpen(true);
    } catch (err) {
      console.error('Error fetching template for send:', err);
      captureError(err, { page: 'MyForms', action: 'handleSend', templateId: template.id });
      showToast('Erreur lors de la préparation du formulaire', 'error');
      setSelectedTemplate(null);
    } finally {
      setSendingId(null);
    }
  };

  const handleEdit = (template: TemplateWithMeta) => {
    navigate(
      template.status === 'draft'
        ? `/my-forms/${template.id}/draft`
        : `/my-forms/${template.id}/edit`
    );
  };

  const handleAssign = (template: TemplateWithMeta) => {
    setTemplateToAssign(template);
    setAssignModalOpen(true);
  };

  const handleMarkRead = async (templateId: string) => {
    if (!user) return;
    const notifIds = unreadNotificationMap.get(templateId);
    if (!notifIds || notifIds.length === 0) return;

    const { error } = await supabase
      .from('notifications')
      .update({ is_read: true })
      .in('id', notifIds)
      .eq('user_id', user.id);

    if (error) {
      console.error('Error marking notifications as read:', error);
      captureError(error, { page: 'MyForms', action: 'handleMarkRead', templateId });
      return;
    }

    setUnreadNotificationMap((prev) => {
      const next = new Map(prev);
      next.delete(templateId);
      return next;
    });
    setTemplates((prev) =>
      prev.map((t) => (t.id === templateId ? { ...t, has_unread_assignment: false } : t))
    );
  };

  const filteredTemplates = templates.filter((t) =>
    t.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <AppLayout
      breadcrumbs={[
        { label: 'Qollect', path: '/dashboard' },
        { label: 'Formulaires' },
      ]}
      actions={
        <div className="flex flex-col sm:flex-row gap-3 w-full items-stretch sm:items-center sm:justify-between">
          {templates.length > 0 && (
            <div className="relative flex-1 sm:max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <input
                type="text"
                placeholder="Rechercher un formulaire..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 text-sm border border-neutral-200 rounded-xl focus:ring-2 focus:ring-primary-600 focus:border-transparent bg-white shadow-soft"
              />
            </div>
          )}
          {isAdmin && canWrite && (
            <Button onClick={() => navigate('/my-forms/new')} className="w-full sm:w-auto shrink-0">
              <Plus className="w-4 h-4 mr-2" />
              Nouveau formulaire
            </Button>
          )}
        </div>
      }
    >
      {loading ? (
        <Card>
          <div className="text-center py-16">
            <div className="text-primary-600 mb-4 flex justify-center">
              <Spinner size="md" />
            </div>
            <p className="text-neutral-500">Chargement...</p>
          </div>
        </Card>
      ) : templates.length === 0 ? (
        <Card>
          <div className="text-center py-16">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-neutral-100 rounded-3xl mb-5">
              <FileText className="w-10 h-10 text-neutral-400" />
            </div>
            <p className="text-neutral-600 mb-2 text-lg font-semibold">Aucun formulaire pour le moment</p>
            <p className="text-sm text-neutral-400 mb-8 max-w-md mx-auto">
              Créez votre premier formulaire réutilisable
            </p>
            {isAdmin && canWrite && (
              <Button onClick={() => navigate('/my-forms/new')}>
                <Plus className="w-4 h-4 mr-2" />
                Créer mon premier formulaire
              </Button>
            )}
          </div>
        </Card>
      ) : filteredTemplates.length === 0 ? (
        <Card>
          <div className="text-center py-16">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-neutral-100 rounded-2xl mb-4">
              <SearchX className="w-8 h-8 text-neutral-400" />
            </div>
            <p className="text-neutral-600 text-lg font-medium mb-2">Aucun résultat</p>
            <p className="text-sm text-neutral-500 mb-6">
              Aucun formulaire ne correspond à "{searchQuery}"
            </p>
            <Button variant="secondary" onClick={() => setSearchQuery('')}>
              Effacer la recherche
            </Button>
          </div>
        </Card>
      ) : (
        <FormsTable
          templates={filteredTemplates}
          isAdmin={isAdmin}
          onSend={handleSend}
          onPreview={(id) => {
            setPreviewTemplateId(id);
            setPreviewModalOpen(true);
          }}
          onEdit={handleEdit}
          onDelete={(id) => {
            setTemplateToDelete(id);
            setDeleteModalOpen(true);
          }}
          onToggleVisibility={handleToggleVisibility}
          onAssign={handleAssign}
          onMarkRead={handleMarkRead}
          sendingId={sendingId}
        />
      )}

      {fullSelectedTemplate && user && (
        <SendFormModal
          isOpen={sendModalOpen}
          onClose={() => {
            setSendModalOpen(false);
            setSelectedTemplate(null);
            setFullSelectedTemplate(null);
          }}
          template={fullSelectedTemplate}
          userId={user.id}
          onSuccess={fetchTemplates}
        />
      )}

      {user && (
        <FormTemplatePreviewModal
          isOpen={previewModalOpen}
          onClose={() => {
            setPreviewModalOpen(false);
            setPreviewTemplateId(null);
          }}
          templateId={previewTemplateId}
          userId={user.id}
          onSendSuccess={fetchTemplates}
          adminMode={isAdmin}
        />
      )}

      {templateToAssign && (
        <AssignFormModal
          isOpen={assignModalOpen}
          onClose={(success) => {
            setAssignModalOpen(false);
            setTemplateToAssign(null);
            if (success) fetchTemplates();
          }}
          templateId={templateToAssign.id}
          templateName={templateToAssign.name}
        />
      )}

      <ConfirmModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setTemplateToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Supprimer le formulaire"
        message="Êtes-vous sûr de vouloir supprimer ce formulaire ? Cette action est irréversible."
        confirmLabel="Supprimer"
        cancelLabel="Annuler"
        variant="danger"
        loading={deletingId !== null}
      />
    </AppLayout>
  );
}
